import {
  normalizeUrl,
  URL_VALIDATION_ERROR,
  validateUrl,
} from "../components/utils/urlHelpers";
import { useNotification } from "../context/NotificationContext";
import { useState } from "../imports";
import { useConfirm } from "../providers/ConfirmProvider";

type UrlEntry = {
  short_code: string;
  original_url: string;
  name: string;
  prefix: string;
  isfavourite: boolean;
  expiration_date?: string | null;
  logo_id?: string;
};
const backendUrl = process.env.REACT_APP_BACKEND_URL;

const useURLActions = (
  setShortenedUrls: any,
  clientPrincipal: any,
  fetchShortenedUrls?: (clientPrincipal: any) => Promise<void>
) => {
  const isLocalhost =
    typeof window !== "undefined" &&
    ["localhost", "127.0.0.1", "::1"].includes(window.location.hostname);
  const localAuthEnabled =
    process.env.REACT_APP_USE_LOCAL_AUTH === "true" && isLocalhost;

  const userId =
    clientPrincipal?.userId ??
    clientPrincipal?.clientPrincipal?.userId ??
    (localAuthEnabled ? "test-user-id" : undefined);
  const [originalUrl, setOriginalUrl] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [expirationDate, setExpirationDate] = useState("");
  const { setNotification } = useNotification();
  const [prefix, setPrefix] = useState<string>("CGI");
  const [selectedLogoId, setSelectedLogoId] = useState<string>("CGI");
  const confirm = useConfirm();

  const [deleteConfirmState, setDeleteConfirmState] = useState<{
    isOpen: boolean;
    shortCode: string | null;
  }>({
    isOpen: false,
    shortCode: null,
  });

  const handleShorten = async (logoId?: string): Promise<string | null> => {
    if (!userId) {
      setNotification({
        type: "error",
        message:
          "Login info is missing. Please refresh the page or login again.",
      });
      return null;
    }

    const res = validateUrl(originalUrl);
    if (!res.ok) {
      setNotification({
        type: "error",
        message: res.error || URL_VALIDATION_ERROR,
      });
      return null;
    }
    const normalizedUrl = res.value;

    if (!name.trim()) {
      setNotification({
        type: "error",
        message: "Please enter a name for the URL",
      });
      return null;
    }

    try {
      const checkResponse = await fetch(`${backendUrl}/urls`, {
        headers: {
          Accept: "application/json",
          "client-principal-user-id": userId,
        },
        credentials: "include",
      });

      if (!checkResponse.ok) {
        setNotification({
          type: "error",
          message: "Failed to check existing URLs.",
        });
        return null;
      }

      const existing: UrlEntry[] = await checkResponse.json();

      const nameExists = existing.some((entry) => entry.name === name);
      if (nameExists) {
        setNotification({
          type: "error",
          message: `The name "${name}" is already in use.`,
        });
        return null;
      }

      const urlExists = existing.some(
        (entry) => normalizeUrl(entry.original_url) === normalizedUrl
      );
      if (urlExists) {
        setNotification({
          type: "error",
          message: `The URL "${normalizedUrl}" has already been shortened.`,
        });
        return null;
      }

      const response = await fetch(`${backendUrl}/shorten`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "client-principal-user-id": userId,
        },
        credentials: "include",
        body: JSON.stringify({
          originalUrl: normalizedUrl,
          name,
          expirationDate,
          prefix,
          logoId: logoId || selectedLogoId,
          user: clientPrincipal,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        const errorMsg = errorData.error?.toLowerCase() || "";

        if (response.status === 409) {
          if (errorMsg.includes("name")) {
            setNotification({
              type: "error",
              message: `The name "${name}" is already in use.`,
            });
          } else if (errorMsg.includes("url")) {
            setNotification({
              type: "error",
              message: `The URL "${normalizedUrl}" has already been shortened.`,
            });
          } else {
            setNotification({
              type: "error",
              message: "This URL or name already exists.",
            });
          }
        } else {
          setNotification({
            type: "error",
            message: "Something went wrong. Please try again.",
          });
        }
        return null;
      }

      const data = await response.json();
      const { shortCode, error } = data;

      if (!shortCode) {
        setNotification({
          type: "error",
          message: error || "Something went wrong.",
        });
        return null;
      }

      setShortenedUrls((prevUrls: any) => ({
        [shortCode]: {
          url: normalizedUrl,
          name,
          fileFormat: "png",
          prefix: prefix,
          isFavourite: false,
          expirationDate,
          logoId: logoId || selectedLogoId,
        },
        ...prevUrls,
      }));

      setNotification({
        type: "success",
        message: "URL shortened successfully!",
      });

      setOriginalUrl("");
      setName("");
      setExpirationDate("");

      return shortCode;
    } catch (error) {
      setNotification({
        type: "error",
        message: "Sorry! Something went wrong.",
      });
      return null;
    }
  };

  const handleCopy = (shortUrl: string) => {
    navigator.clipboard
      .writeText(shortUrl)
      .then(() => {
        setNotification({
          type: "success",
          message: "Shortened URL copied to clipboard!",
        });
      })
      .catch(() => {
        setNotification({
          type: "error",
          message: "There was an error copying the URL.",
        });
      });
  };

  const handleDelete = async (shortCode: string, urlName?: string) => {
    const confirmed = await confirm({
      title: "Delete URL",
      message: urlName
        ? `Are you sure you want to delete "${urlName}"?`
        : "Are you sure you want to delete this shortened URL?",
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
    });

    if (!confirmed) return;

    try {
      const response = await fetch(`${backendUrl}/delete/${shortCode}`, {
        method: "DELETE",
        credentials: "include",
        headers: {
          "client-principal-user-id": userId,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        setNotification({ type: "error", message: JSON.stringify(errorData) });
        return;
      }

      setShortenedUrls((prevUrls: any) => {
        const updatedUrls = { ...prevUrls };
        delete updatedUrls[shortCode];
        return updatedUrls;
      });

      setNotification({
        type: "success",
        message: "URL deleted successfully!",
      });
    } catch (error) {
      setNotification({
        type: "error",
        message: "Fehler beim Löschen der URL.",
      });
    }
  };

  const confirmDelete = async () => {
    const { shortCode } = deleteConfirmState;

    if (!shortCode) return;

    try {
      const response = await fetch(`${backendUrl}/delete/${shortCode}`, {
        method: "DELETE",
        credentials: "include",
        headers: {
          "client-principal-user-id": userId,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        setNotification({ type: "error", message: JSON.stringify(errorData) });
        return;
      }

      setShortenedUrls((prevUrls: any) => {
        const updatedUrls = { ...prevUrls };
        delete updatedUrls[shortCode];
        return updatedUrls;
      });

      setNotification({
        type: "success",
        message: "URL deleted successfully!",
      });
    } catch (error) {
      setNotification({
        type: "error",
        message: "Fehler beim Löschen der URL.",
      });
    } finally {
      setDeleteConfirmState({
        isOpen: false,
        shortCode: null,
      });
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmState({
      isOpen: false,
      shortCode: null,
    });
  };

  const handleToggleFavourite = async (
    shortCode: string,
    currentStatus: boolean
  ) => {
    try {
      const newStatus = !currentStatus;

      setShortenedUrls((prev: any) => {
        const updatedUrls = { ...prev };
        if (updatedUrls[shortCode]) {
          updatedUrls[shortCode] = {
            ...updatedUrls[shortCode],
            isFavourite: newStatus,
          };
        }
        return updatedUrls;
      });

      const response = await fetch(
        `${backendUrl}/toggle-favourite/${shortCode}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "client-principal-user-id": userId,
          },
          credentials: "include",
          body: JSON.stringify({ isFavourite: newStatus }),
        }
      );

      if (!response.ok) {
        setShortenedUrls((prev: any) => {
          const revertedUrls = { ...prev };
          if (revertedUrls[shortCode]) {
            revertedUrls[shortCode] = {
              ...revertedUrls[shortCode],
              isFavourite: currentStatus,
            };
          }
          return revertedUrls;
        });

        const errorData = await response.json();
        setNotification({
          type: "error",
          message: errorData.message || "Failed to update favourite status.",
        });
        return;
      }

      setNotification({
        type: "success",
        message: "Favourite status updated!",
      });
    } catch (error: any) {
      setShortenedUrls((prev: any) => {
        const revertedUrls = { ...prev };
        if (revertedUrls[shortCode]) {
          revertedUrls[shortCode] = {
            ...revertedUrls[shortCode],
            isFavourite: currentStatus,
          };
        }
        return revertedUrls;
      });

      setNotification({
        type: "error",
        message: "Could not change favourite status.",
      });
    }
  };
  const handleUpdateUrl = async (
    shortCode: string,
    newUrl: string,
    newName: string,
    oldUrl: string,
    oldName: string,
    newExpirationDate: string | null,
    customColor: string,
    withLogo: boolean,
    newPrefix: string,
    selectedLogoId?: string | null
  ) => {
    try {
      const candidate = newUrl.trim() === "" ? oldUrl : newUrl.trim();
      const updatedName = newName.trim() === "" ? oldName : newName.trim();

      const v = validateUrl(candidate);
      if (!v.ok) {
        setNotification({ type: "error", message: "The new URL is invalid." });
        return;
      }
      const updatedUrl = v.value;

      const responseCheck = await fetch(`${backendUrl}/urls`, {
        headers: {
          Accept: "application/json",
          "client-principal-user-id": userId,
        },
        credentials: "include",
      });

      if (!responseCheck.ok) {
        setNotification({
          type: "error",
          message: "An error occurred while fetching the saved URLs.",
        });
        return;
      }

      const existingUrls: UrlEntry[] = await responseCheck.json();

      const urlExists = existingUrls.some(
        (entry) =>
          normalizeUrl(entry.original_url) === updatedUrl &&
          updatedUrl !== normalizeUrl(oldUrl)
      );

      if (urlExists) {
        setNotification({
          type: "error",
          message: "This short URL is already in use.",
        });
        return;
      }

      const nameExists = existingUrls.some(
        (entry) => entry.name === updatedName && updatedName !== oldName
      );

      if (nameExists) {
        setNotification({
          type: "error",
          message: "This name is already in use.",
        });
        return;
      }

      const response = await fetch(`${backendUrl}/update/${shortCode}`, {
        method: "PUT",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "client-principal-user-id": userId,
        },
        credentials: "include",
        body: JSON.stringify({
          originalUrl: updatedUrl,
          name: updatedName,
          expirationDate: newExpirationDate,
          customColor,
          withLogo,
          prefix: newPrefix,
          selectedLogoId: selectedLogoId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        setNotification({
          type: "error",
          message:
            errorData.message || "An error occurred while updating the URL",
        });
        return;
      }

      if (fetchShortenedUrls) {
        await fetchShortenedUrls(clientPrincipal?.userId);
      }

      setNotification({
        type: "success",
        message: "URL updated successfully!",
      });
    } catch (error: any) {
      setNotification({
        type: "error",
        message: "Failed to update the URL.",
      });
    }
  };

  return {
    originalUrl,
    setOriginalUrl,
    name,
    setName,
    expirationDate,
    setExpirationDate,
    selectedLogoId,
    setSelectedLogoId,
    prefix,
    setPrefix,
    handleShorten,
    handleDelete,
    confirmDelete,
    cancelDelete,
    deleteConfirmState,
    handleToggleFavourite,
    handleCopy,
    handleUpdateUrl,
  };
};

export default useURLActions;
